import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Shield, TrendingUp, Users, Briefcase, FileText, BarChart3, AlertTriangle } from 'lucide-react';
import OccupationRiskTracker from './components/OccupationRiskTracker';
import EnterpriseTracker from './components/EnterpriseTracker';
import PartnershipsTracker from './components/PartnershipsTracker';
import InfrastructureTracker from './components/InfrastructureTracker';
import PolicyTracker from './components/PolicyTracker';
import EmploymentDashboard from './components/EmploymentDashboard';
import McKinseyTimeline from './components/McKinseyTimeline';
import './App.css';

function App() {
  return (
    <Router basename="/app">
      <div className="min-h-screen bg-gray-50">
        {/* Navigation */}
        <nav className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <Shield className="h-8 w-8 text-blue-600" />
                <div>
                  <div className="text-xl font-bold">PivotIntel</div>
                  <div className="text-xs text-gray-500">Intelligence Trackers</div>
                </div>
              </div>
              
              <div className="flex gap-6">
                <Link to="/" className="text-gray-700 hover:text-blue-600 text-sm font-medium">
                  Home
                </Link>
                <a href="https://pivotintel.org" className="text-gray-700 hover:text-blue-600 text-sm font-medium">
                  Main Site
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <Routes>
          <Route path="/" element={<TrackerHome />} />
          <Route path="/occupation-risk" element={<OccupationRiskTracker />} />
          <Route path="/enterprise" element={<EnterpriseTracker />} />
          <Route path="/partnerships" element={<PartnershipsTracker />} />
          <Route path="/infrastructure" element={<InfrastructureTracker />} />
          <Route path="/policy" element={<PolicyTracker />} />
          <Route path="/employment" element={<EmploymentDashboard />} />
          <Route path="/timeline" element={<McKinseyTimeline />} />
        </Routes>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 mt-16">
          <div className="max-w-7xl mx-auto px-4 py-8 text-center text-sm text-gray-600">
            <p>© 2025 PivotIntel | <a href="https://pivotintel.org/about/mission" className="text-blue-600 hover:text-blue-800">Mission</a> | Last Updated: {new Date().toLocaleDateString()}</p>
          </div>
        </footer>
      </div>
    </Router>
  );
}

// Tracker Home/Dashboard
function TrackerHome() {
  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">AI Intelligence Trackers</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Real-time tracking of AI infrastructure, enterprise adoption, partnerships, 
          policy, and employment impact. Updated daily.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Occupation Risk Tracker */}
        <Link to="/occupation-risk" onClick={() => window.scrollTo(0, 0)} className="bg-white p-6 rounded-lg shadow-sm border-2 border-transparent hover:border-red-200 transition-all">
          <div className="bg-red-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-xl font-bold mb-2">Occupation Risk Tracker</h3>
          <p className="text-gray-600 mb-4">
            AI automation risk across all major job categories. See which occupations 
            are protected and which face displacement.
          </p>
          <div className="text-blue-600 font-semibold">View Tracker →</div>
        </Link>

        {/* Enterprise Tracker */}
        <Link to="/enterprise" onClick={() => window.scrollTo(0, 0)} className="bg-white p-6 rounded-lg shadow-sm border-2 border-transparent hover:border-purple-200 transition-all">
          <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
            <Briefcase className="h-6 w-6 text-purple-600" />
          </div>
          <h3 className="text-xl font-bold mb-2">Enterprise Adoption</h3>
          <p className="text-gray-600 mb-4">
            Companies deploying AI to automate work. Track which organizations 
            are adopting and how many workers are affected.
          </p>
          <div className="text-blue-600 font-semibold">View Tracker →</div>
        </Link>

        {/* Partnerships Tracker */}
        <Link to="/partnerships" onClick={() => window.scrollTo(0, 0)} className="bg-white p-6 rounded-lg shadow-sm border-2 border-transparent hover:border-green-200 transition-all">
          <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
            <Users className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-xl font-bold mb-2">Partnerships & Deals</h3>
          <p className="text-gray-600 mb-4">
            Strategic partnerships, compute contracts, and joint ventures 
            driving AI infrastructure buildout.
          </p>
          <div className="text-blue-600 font-semibold">View Tracker →</div>
        </Link>

        {/* Infrastructure Tracker */}
        <Link to="/infrastructure" onClick={() => window.scrollTo(0, 0)} className="bg-white p-6 rounded-lg shadow-sm border-2 border-transparent hover:border-blue-200 transition-all">
          <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
            <TrendingUp className="h-6 w-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold mb-2">Infrastructure Projects</h3>
          <p className="text-gray-600 mb-4">
            Data center projects nationwide. Investment amounts, job claims, 
            community impact, and current status.
          </p>
          <div className="text-blue-600 font-semibold">View Tracker →</div>
        </Link>

        {/* Policy Tracker */}
        <Link to="/policy" onClick={() => window.scrollTo(0, 0)} className="bg-white p-6 rounded-lg shadow-sm border-2 border-transparent hover:border-orange-200 transition-all">
          <div className="bg-orange-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
            <FileText className="h-6 w-6 text-orange-600" />
          </div>
          <h3 className="text-xl font-bold mb-2">Policy Developments</h3>
          <p className="text-gray-600 mb-4">
            Government actions affecting AI infrastructure and worker protection. 
            Track what enables buildout vs protects workers.
          </p>
          <div className="text-blue-600 font-semibold">View Tracker →</div>
        </Link>

        {/* Employment Dashboard */}
        <Link to="/employment" onClick={() => window.scrollTo(0, 0)} className="bg-white p-6 rounded-lg shadow-sm border-2 border-transparent hover:border-red-200 transition-all">
          <div className="bg-red-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
            <BarChart3 className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-xl font-bold mb-2">Employment Data</h3>
          <p className="text-gray-600 mb-4">
            Weekly employment metrics showing the two-economy split. 
            Track job losses in automation-vulnerable sectors.
          </p>
          <div className="text-blue-600 font-semibold">View Tracker →</div>
        </Link>

              {/* McKinsey Timeline */}
        <Link to="/timeline" onClick={() => window.scrollTo(0, 0)} className="bg-white p-6 rounded-lg shadow-sm border-2 border-transparent hover:border-yellow-200 transition-all">
          <div className="bg-yellow-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
            <TrendingUp className="h-6 w-6 text-yellow-600" />
          </div>
          <h3 className="text-xl font-bold mb-2">McKinsey Timeline</h3>
          <p className="text-gray-600 mb-4">
            See how automation assessments changed from 2017 → 2023 → 2025. 
            Why weekly updates beat 2-year research cycles.
          </p>
          <div className="text-blue-600 font-semibold">View Timeline →</div>
        </Link>
      </div>

      <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
        <h3 className="text-2xl font-bold mb-4">Get Weekly Intelligence</h3>
        <p className="text-gray-700 mb-6">
          These trackers update automatically. Get weekly analysis delivered to your inbox.
        </p>
        <div className="flex gap-4 justify-center">
          <a href="https://pivotintel.org" className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700">
            Subscribe to Newsletters
          </a>
        </div>
      </div>
    </div>
  );
}

export default App;